/**
 * Avatar utility functions for handling user profile images
 */

import { User } from 'lucide-react'

/**
 * Default avatar placeholder - can be an SVG data URI or a public URL
 */
export const DEFAULT_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23a855f7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"%3E%3C/path%3E%3Ccircle cx="12" cy="7" r="4"%3E%3C/circle%3E%3C/svg%3E'

/**
 * Get a safe avatar URL with fallback to placeholder
 * @param avatarUrl - The avatar URL from the database
 * @param fallback - Optional custom fallback image
 * @returns A valid image URL or placeholder
 */
export function getSafeAvatarUrl(
  avatarUrl: string | null | undefined,
  fallback: string = DEFAULT_AVATAR
): string {
  // Return fallback if no avatar URL provided
  if (!avatarUrl || avatarUrl.trim() === '') {
    return fallback
  }

  // Check if it's a valid URL
  try {
    // Handle relative URLs (from Supabase storage)
    if (avatarUrl.startsWith('/')) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (supabaseUrl) {
        return `${supabaseUrl}/storage/v1/object/public${avatarUrl}`
      }
    }
    
    // Handle full URLs
    const url = new URL(avatarUrl)
    return avatarUrl
  } catch {
    // If URL parsing fails, check if it's a Supabase storage path
    if (avatarUrl.includes('storage/v1/object/public')) {
      return avatarUrl
    }
    
    // Otherwise return fallback
    return fallback
  }
}

/**
 * Generate initials from a name
 * @param name - Display name or username
 * @returns Two letter initials
 */
export function getInitials(name: string | null | undefined): string {
  if (!name || name.trim() === '') {
    return 'U'
  }
  
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Generate a color based on a string (for avatar backgrounds)
 * @param str - String to generate color from (e.g., username, ID)
 * @returns Hex color code
 */
export function stringToColor(str: string | null | undefined): string {
  if (!str) return '#6B7280' // Default gray
  
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  const colors = [
    '#EF4444', // red
    '#F59E0B', // amber
    '#10B981', // emerald
    '#3B82F6', // blue
    '#8B5CF6', // violet
    '#EC4899', // pink
    '#14B8A6', // teal
    '#F97316', // orange
  ]
  
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Check if an image URL is valid and loadable
 * @param url - Image URL to check
 * @returns Promise that resolves to boolean
 */
export async function isImageValid(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}

/**
 * Default event placeholder - SVG showing calendar/event icon
 */
export const DEFAULT_EVENT_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect x="3" y="4" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Cline x1="16" y1="2" x2="16" y2="6"%3E%3C/line%3E%3Cline x1="8" y1="2" x2="8" y2="6"%3E%3C/line%3E%3Cline x1="3" y1="10" x2="21" y2="10"%3E%3C/line%3E%3C/svg%3E'

/**
 * Get a safe event image URL with fallback to placeholder
 * @param imageUrl - The event image URL from the database
 * @param fallback - Optional custom fallback image
 * @returns A valid image URL or placeholder
 */
export function getSafeEventImageUrl(
  imageUrl: string | null | undefined,
  fallback: string = DEFAULT_EVENT_IMAGE
): string {
  // Return fallback if no image URL provided
  if (!imageUrl || imageUrl.trim() === '') {
    return fallback
  }

  // Check if it's a valid URL
  try {
    // Handle relative URLs (from Supabase storage)
    if (imageUrl.startsWith('/')) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (supabaseUrl) {
        return `${supabaseUrl}/storage/v1/object/public${imageUrl}`
      }
    }
    
    // Handle full URLs
    const url = new URL(imageUrl)
    return imageUrl
  } catch {
    // If URL parsing fails, check if it's a Supabase storage path
    if (imageUrl.includes('storage/v1/object/public')) {
      return imageUrl
    }
    
    // Check for placeholder API patterns and replace with our SVG
    if (imageUrl.includes('/api/placeholder') || imageUrl.includes('placeholder')) {
      return fallback
    }
    
    // Otherwise return fallback
    return fallback
  }
}

/**
 * Generate event placeholder based on event type or category
 * @param eventType - Type of event (party, workshop, social, exclusive)
 * @param category - Event category
 * @returns Appropriate placeholder image URL
 */
export function getEventPlaceholder(eventType?: string, category?: string): string {
  const type = (eventType || category || '').toLowerCase()
  
  // Party events
  if (type.includes('party') || type.includes('dance') || type.includes('club')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M8 9l4-4 4 4"%3E%3C/path%3E%3Cpath d="M12 16v6"%3E%3C/path%3E%3Cpath d="M8 22h8"%3E%3C/path%3E%3Cpath d="M8 22v-3"%3E%3C/path%3E%3Cpath d="M16 22v-3"%3E%3C/path%3E%3C/svg%3E'
  }
  
  // Workshop/educational events
  if (type.includes('workshop') || type.includes('class') || type.includes('education')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"%3E%3C/path%3E%3Cpath d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"%3E%3C/path%3E%3C/svg%3E'
  }
  
  // Social/networking events
  if (type.includes('social') || type.includes('meet') || type.includes('networking')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"%3E%3C/path%3E%3Ccircle cx="9" cy="7" r="4"%3E%3C/circle%3E%3Cpath d="M23 21v-2a4 4 0 0 0-3-3.87"%3E%3C/path%3E%3Cpath d="M16 3.13a4 4 0 0 1 0 7.75"%3E%3C/path%3E%3C/svg%3E'
  }
  
  // Exclusive/premium events
  if (type.includes('exclusive') || type.includes('vip') || type.includes('premium')) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"%3E%3C/path%3E%3C/svg%3E'
  }
  
  // Default calendar placeholder
  return DEFAULT_EVENT_IMAGE
}